/**
 * W-12 订单列表(WScreen-12)。双 tab [我的购买][我的售出];买家扁平倒序 / 卖家按组。
 * 状态 Tag / 分组 / 「去支付」按 state-machine §一/§四。空态无 CTA。视觉真源 design/orders.html。
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { listMyOrders, type OrderListItem, type OrderRole } from '@/api/order';
import {
  STATUS_TONE_CLASS,
  buildOrderRows,
  errorCode,
  formatAmount,
  formatCreatedAtShort,
  resolveStateBadge,
} from '@/lib/orderState';

const PAGE_SIZE = 20;
const TAB_STORAGE_KEY = 'workability-web-orders-tab';

function readInitialTab(): OrderRole {
  try {
    return localStorage.getItem(TAB_STORAGE_KEY) === 'seller' ? 'seller' : 'buyer';
  } catch {
    return 'buyer';
  }
}

export function OrdersPage() {
  const [role, setRole] = useState<OrderRole>(readInitialTab);

  const switchTab = (next: OrderRole) => {
    setRole(next);
    try {
      localStorage.setItem(TAB_STORAGE_KEY, next);
    } catch {
      /* 隐私模式忽略 */
    }
  };

  return (
    <div className="inner section">
      <h1 className="h2 mb-6">我的订单</h1>

      <div className="tabs" role="tablist">
        <span
          role="tab"
          aria-selected={role === 'buyer'}
          className={`tab${role === 'buyer' ? ' active' : ''}`}
          style={{ cursor: 'pointer' }}
          onClick={() => switchTab('buyer')}
        >
          我的购买
        </span>
        <span
          role="tab"
          aria-selected={role === 'seller'}
          className={`tab${role === 'seller' ? ' active' : ''}`}
          style={{ cursor: 'pointer' }}
          onClick={() => switchTab('seller')}
        >
          我的售出
        </span>
      </div>

      {/* 切 tab 仅挂当前视角列表(key 强制重挂,停旧查询) */}
      <OrderList key={role} role={role} />
    </div>
  );
}

function OrderList({ role }: { role: OrderRole }) {
  const navigate = useNavigate();
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['my-orders', role],
    queryFn: ({ pageParam }) => listMyOrders({ role, page: pageParam, size: PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
  });

  if (isLoading) {
    return (
      <div className="state" data-testid={`orders-loading-${role}`}>
        <p className="muted sm" style={{ color: 'var(--color-primary)' }}>
          加载中…
        </p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="state">
        <p className="sm" style={{ color: 'var(--color-destructive)' }}>
          加载失败({errorCode(error)})
        </p>
        <button type="button" className="btn btn-secondary mt-4" onClick={() => refetch()}>
          点此重试
        </button>
      </div>
    );
  }

  const items = data?.pages.flatMap((p) => p.items) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  if (total === 0) {
    // 空态:无任何 CTA(尤其售出态禁止「去发布」引导)。
    return (
      <div className="state">
        <p className="muted sm">{role === 'buyer' ? '暂无购买订单' : '暂无售出订单'}</p>
      </div>
    );
  }

  const rows = buildOrderRows(items, role);

  return (
    <>
      <section className="order-list">
        {rows.map((row) =>
          row.type === 'section' ? (
            <div key={row.key} className="order-group-label">
              {row.label}
            </div>
          ) : (
            <OrderCard
              key={row.key}
              order={row.order}
              onOpen={() => navigate(`/orders/${row.order.orderId}`)}
            />
          ),
        )}
      </section>

      <div className="state" style={{ padding: 'var(--space-8) var(--space-4)' }}>
        {isFetchingNextPage ? (
          <span className="xs muted">加载中…</span>
        ) : hasNextPage ? (
          <button type="button" className="btn btn-secondary" onClick={() => fetchNextPage()}>
            加载更多
          </button>
        ) : (
          <span className="xs muted">没有更多了</span>
        )}
      </div>
    </>
  );
}

function OrderCard({ order, onOpen }: { order: OrderListItem; onOpen: () => void }) {
  const badge = resolveStateBadge(order.state, order.subState);
  const showPay = order.state === 'PENDING_PAYMENT';
  return (
    <div
      className="order-card"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault(); // 空格键不滚动页面
          onOpen();
        }
      }}
    >
      {/* 头部:下单时间(左) · 状态(右)—— 业界订单卡通行结构 */}
      <div className="order-card__head">
        <span className="order-card__meta">下单于 {formatCreatedAtShort(order.createdAt)}</span>
        <span className={STATUS_TONE_CLASS[badge.tone]}>{badge.label}</span>
      </div>

      {/* 体部:封面 + 标题/对方 + 价格/操作 */}
      <div className="order-card__body">
        <div className="order-card__cover">
          {order.coverImageUrl ? <img src={order.coverImageUrl} alt="" /> : '封面'}
        </div>
        <div className="order-card__info">
          <span className="order-card__title">{order.productTitle || '商品已下架'}</span>
          <span className="xs muted">对方 {order.counterparty.nickname}</span>
        </div>
        <div className="order-card__right">
          <span className="price">{formatAmount(order.amount)}</span>
          {showPay && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                onOpen(); // 去支付 → 详情续付落点(不直跳收银台)
              }}
            >
              去支付
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
